import styled from '@emotion/styled';
import { IPublicCompany, industryImageMapping, industryMapping, getKeyByValue } from 'domain/entities/Company/Company';
import { MATCH_STATUSES } from 'domain/entities/Match/Match';
import PointIcon from 'ui/images/point.svg';
import BsGraphUpIcon from 'ui/images/bs_graph_up.svg';
import BsPeopleIcon from 'ui/images/bs_people.svg';
import NoImage from 'ui/images/no_image_case.png';
import { mqUser } from 'utils/responsive';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import dayjs from 'dayjs';

interface PublicCompanyCardProps {
    company: IPublicCompany;
    showCompanyTypeLabel?: boolean;
    onClick: () => void;
}

const Card = styled.div`
    width: calc(50% - 10px);
    background: white;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    gap: 20px;
    cursor: pointer;

    &:hover {
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
    }

    ${mqUser('SP')} {
        width: 100%;
        flex-direction: column;
        gap: 12px;
    }
`;

const ImgContainer = styled.div`
    width: 132px;
    height: 132px;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    position: relative;

    img {
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        border-radius: 8px;
        object-fit: cover;
        display: block;
    }

    ${mqUser('SP')} {
        width: 100%;
        height: auto;
        aspect-ratio: 1 / 1;
    }
`;

const Detail = styled.div`
    display: flex;
    gap: 8px;
    flex-direction: column;
`;

const Label = styled.div`
    padding: 5px;
    gap: 4px;
    height: fit-content;
    border-radius: 4px;
    background-color: #f3f3f3;
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #222222;
    font-weight: 500;

    img {
        flex-shrink: 0;
    }

    ${mqUser('SP')} {
        word-break: break-word;
        white-space: normal;
        flex-wrap: wrap;
    }
`;

const LabelContainer = styled.div`
    display: flex;
    gap: 5px;
    flex-wrap: wrap;

    ${mqUser('SP')} {
        gap: 6px;
    }
`;

const IndustryLabel = styled.div`
    font-weight: 700;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 10%;
`;

const ImageLabel = styled.div`
    position: absolute;
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
    border-radius: 30px;
    padding: 5px;
    font-weight: 700;
    font-size: 12px;
    line-height: 14.4px;
    letter-spacing: 0%;
    white-space: nowrap;
    color: white;
    background-color: #ee793f;
`;

const CompanyLabel = styled.div`
    position: absolute;
    top: 0px;
    left: 0px;
    border-radius: 8px 0 8px 0;
    padding: 5px;
    font-weight: 700;
    font-size: 12px;
    line-height: 14.4px;
    letter-spacing: 0%;
    white-space: nowrap;
    color: white;
    background-color: var(--main-color-blue);
`;

const CreatedAt = styled.div`
    font-size: 12px;
    color: #222222;
    font-weight: 500;
`;

const BottomContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
`;

const UserTypeLabel = styled.div`
    position: absolute;
    bottom: 50%;
    left: 50%;
    transform: translate(-50%, 50%);
    white-space: nowrap;
`;

const getIndustryImage = (industry: string | null) => {
    if (!industry) {
        return NoImage;
    }
    const industryKey = getKeyByValue(industryMapping, industry);
    const industryImage = industryImageMapping[industryKey as keyof typeof industryImageMapping];
    return industryImage;
};

export const PublicCompanyCard: React.FC<PublicCompanyCardProps> = ({
    company,
    showCompanyTypeLabel = false,
    onClick,
}) => {
    return (
        <Card onClick={onClick}>
            <ImgContainer>
                <img src={getIndustryImage(company.industry)} alt={company.industry} />
                {showCompanyTypeLabel && (
                    <UserTypeLabel>
                        <ColorLabel color="#fff" backgroundColor="var(--main-color-blue)">
                            売り手企業
                        </ColorLabel>
                    </UserTypeLabel>
                )}
                {!showCompanyTypeLabel && company?.match_status && (
                    <ImageLabel>
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT && 'オファー済み'}
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_CONNECTED && 'マッチング済み'}
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_EXPIRED && 'オファー期限切れ'}
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_DECLINED && 'オファー辞退'}
                    </ImageLabel>
                )}
                {company?.label && <CompanyLabel>{company.label}</CompanyLabel>}
            </ImgContainer>
            <Detail>
                <IndustryLabel>{company?.industry || '業種未設定'}</IndustryLabel>
                <LabelContainer>
                    <Label>
                        <img src={PointIcon} alt="ピンアイコン" />
                        エリア | {company.area_name}
                    </Label>
                    <Label>
                        <img src={BsGraphUpIcon} alt="グラフアイコン" />
                        売上高 | {company.revenue_scale}
                    </Label>
                    <Label>
                        <img src={BsPeopleIcon} alt="人アイコン" />
                        従業員規模 | {company.number_of_employees}
                    </Label>
                    <Label>
                        <img src={BsGraphUpIcon} alt="グラフアイコン" />
                        営業利益 | {company.profit_level}
                    </Label>
                    <Label>
                        <img src={BsGraphUpIcon} alt="グラフアイコン" />
                        純資産金額 | {company.net_assets_level}
                    </Label>
                </LabelContainer>
                <BottomContainer>
                    <div>
                        {company.is_sole_proprietor && (
                            <ColorLabel color="#008000" backgroundColor="#d3ffa8">
                                個人事業主
                            </ColorLabel>
                        )}
                    </div>
                    <CreatedAt>最終更新日：{dayjs(company.updated_at).format('YYYY/MM/DD')}</CreatedAt>
                </BottomContainer>
            </Detail>
        </Card>
    );
};
